// src/services/api/auth.service.ts
// SECURITY: Tokens are now stored in HTTP-only cookies

import { apiClient, makeApiRequest } from './client';
import { endpoints } from './config';
import {
  LoginRequest,
  LoginResponse,
  User
} from './types';

export class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<LoginResponse>(
      endpoints.auth.login,
      credentials
    );

    // SECURITY: Token is now in HTTP-only cookie, just store expiration for tracking
    if (response.data.expires_in) {
      this.storeExpiration(response.data.expires_in);
    }

    return response.data;
  }

  async refreshToken(): Promise<boolean> {
    // SECURITY: Refresh token is in HTTP-only cookie
    const response = await apiClient.post<LoginResponse>(endpoints.auth.refresh);

    if (response.data.expires_in) {
      this.storeExpiration(response.data.expires_in);
    }

    return true;
  }

  async logout(): Promise<void> {
    try {
      // SECURITY: Server will clear HTTP-only cookies
      await apiClient.post(endpoints.auth.logout);
    } finally {
      this.clearSession();
    }
  }

  async getCurrentUser(): Promise<User> {
    return makeApiRequest(() =>
      apiClient.get<User>(endpoints.auth.me)
    );
  }

  // Session management (no token storage)
  private storeExpiration(expiresIn: number): void {
    localStorage.setItem('token_expires_at',
      (Date.now() + expiresIn * 1000).toString()
    );
  }

  private clearSession(): void {
    localStorage.removeItem('token_expires_at');
    localStorage.removeItem('globalUser');
  }

  isTokenExpired(): boolean {
    const expiresAt = localStorage.getItem('token_expires_at');
    if (!expiresAt) return true;

    return Date.now() > parseInt(expiresAt);
  }

  // Initialize auth state (call this on app startup)
  initializeAuth(): boolean {
    // SECURITY: Token is in HTTP-only cookie, we can only check expiration
    if (!this.isTokenExpired()) {
      return true;
    }
    this.clearSession();
    return false;
  }

  // Auto-refresh token if needed
  async ensureValidToken(): Promise<boolean> {
    if (!this.isTokenExpired()) {
      return true;
    }

    try {
      await this.refreshToken();
      return true;
    } catch (error) {
      this.clearSession();
      return false;
    }
  }
}

export const authService = new AuthService();
